/*
  KEVO product helpers.

  <kevo-buy-proxy>  A secondary purchase button (final CTA, sticky bar) that never duplicates the
                    product form. It mirrors the main product section's live price, selected
                    variant and add-to-cart state, and forwards clicks to Dawn's own submit
                    button, so variants, quantity rules, inventory checks and the cart drawer
                    behave exactly like the main form.

  <kevo-sticky-buy> Shown along the product page, except while the main add-to-cart button is
                    clearly visible and tappable: at least 60% of it inside the area that is not
                    covered by the sticky header or by the bar itself. Also hidden while another
                    KEVO purchase button is visible. Accessibility: while shown it reserves its
                    height as scroll padding and scrolls focused elements out from under it
                    (WCAG 2.4.11); it never hides while it holds keyboard focus (WCAG 2.4.3).
*/
(() => {
  if (customElements.get('kevo-buy-proxy')) return;

  const findMainProduct = (productId) =>
    Array.from(document.querySelectorAll('product-info[id^="MainProduct-"]')).find(
      (element) => !productId || element.dataset.productId === String(productId)
    ) || null;

  class KevoBuyProxy extends HTMLElement {
    connectedCallback() {
      this.init();
    }

    init() {
      this.observer?.disconnect();
      this.button = this.querySelector('[data-kevo-buy-button]');
      this.fallback = this.querySelector('[data-kevo-buy-fallback]');
      this.main = findMainProduct(this.dataset.productId);

      if (!this.button || !this.main || !this.mainButton) {
        this.showFallback();
        return;
      }

      this.priceTarget = this.querySelector('[data-kevo-price]');
      this.variantTarget = this.querySelector('[data-kevo-variant]');
      this.labelTarget = this.button.querySelector('[data-kevo-buy-label]');
      this.spinner = this.button.querySelector('.loading__spinner');
      this.customLabel = this.hasAttribute('data-custom-label') ? this.labelTarget?.textContent.trim() : null;

      if (!this.handleClick) {
        this.handleClick = this.onClick.bind(this);
        this.button.addEventListener('click', this.handleClick);
      }

      // Dawn re-renders price, buttons and variant data in place on every variant change.
      this.observer = new MutationObserver(() => this.scheduleSync());
      this.observer.observe(this.main, {
        subtree: true,
        childList: true,
        characterData: true,
        attributes: true,
        attributeFilter: ['disabled', 'class'],
      });
      this.sync();
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      if (this.handleClick) this.button?.removeEventListener('click', this.handleClick);
      this.handleClick = null;
      if (this.frame) cancelAnimationFrame(this.frame);
    }

    get sectionId() {
      return this.main?.dataset.section;
    }

    get mainButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    showFallback() {
      if (this.button) this.button.hidden = true;
      if (this.fallback) this.fallback.hidden = false;
    }

    scheduleSync() {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.sync();
      });
    }

    sync() {
      const mainButton = this.mainButton;
      if (!mainButton) {
        this.showFallback();
        return;
      }

      // Availability and label ("Add to cart" / "Sold out" / "Unavailable").
      const disabled = mainButton.disabled || mainButton.getAttribute('aria-disabled') === 'true';
      this.button.disabled = disabled;
      const mainLabel = mainButton.querySelector('span')?.textContent.trim();
      const label = !disabled && this.customLabel ? this.customLabel : mainLabel;
      if (label && this.labelTarget && this.labelTarget.textContent !== label) this.labelTarget.textContent = label;

      // Loading state while the main form submits.
      const loading = mainButton.classList.contains('loading');
      this.button.classList.toggle('loading', loading);
      this.button.setAttribute('aria-busy', String(loading));
      this.spinner?.classList.toggle('hidden', !loading);

      // Live price: the selected bundle total when a bundle selector is used, else the variant price.
      const bundleTotal = document.getElementById(`KevoBundleTotal-${this.sectionId}`);
      if (this.priceTarget && bundleTotal && bundleTotal.textContent.trim()) {
        const html = `<span class="price price--kevo-bundle"><span class="price-item">${bundleTotal.textContent.trim()}</span></span>`;
        if (html !== this.lastPrice) {
          this.lastPrice = html;
          this.priceTarget.innerHTML = html;
        }
      } else {
        const price = document.getElementById(`price-${this.sectionId}`);
        if (this.priceTarget && price && price.innerHTML !== this.lastPrice) {
          this.lastPrice = price.innerHTML;
          this.priceTarget.innerHTML = price.innerHTML;
        }
      }

      // Selected variant title.
      if (this.variantTarget) {
        const variantData = this.main.querySelector('variant-selects [data-selected-variant]');
        if (variantData) {
          try {
            const variant = JSON.parse(variantData.textContent);
            const title = variant && variant.title !== 'Default Title' ? variant.title : '';
            if (this.variantTarget.textContent !== title) {
              this.variantTarget.textContent = title;
              this.variantTarget.hidden = !title;
            }
          } catch (error) {
            // Leave the server-rendered variant title in place.
          }
        }
      }
    }

    onClick(event) {
      event.preventDefault();
      if (!this.main?.isConnected) this.init();
      const mainButton = this.mainButton;
      if (!mainButton || mainButton.disabled) return;
      mainButton.click();
    }
  }

  customElements.define('kevo-buy-proxy', KevoBuyProxy);

  // Theme editor: the main product section can be re-rendered while KEVO sections stay in place.
  document.addEventListener('shopify:section:load', () => {
    document.querySelectorAll('kevo-buy-proxy').forEach((proxy) => proxy.init());
    document.querySelectorAll('kevo-sticky-buy').forEach((sticky) => {
      sticky.disconnectedCallback();
      sticky.connectedCallback();
    });
  });

  class KevoStickyBuy extends HTMLElement {
    connectedCallback() {
      this.main = findMainProduct(this.dataset.productId);
      this.mainButton = this.main?.querySelector('[id^="ProductSubmitButton-"]');
      if (!this.mainButton) return;

      // Show the same title as the product hero (its "display title" setting, e.g. "KEVO Balance").
      const heroTitle = this.main.querySelector('.product__title h1')?.textContent.trim();
      const stickyTitle = this.querySelector('[data-kevo-sticky-title]');
      if (heroTitle && stickyTitle) stickyTitle.textContent = heroTitle;

      this.mainClearlyVisible = true;
      this.visibleGuards = new Set();

      // Main button visibility is measured against the viewport minus the sticky header and the
      // bar's own area, on scroll / resize (an IntersectionObserver cannot follow the header,
      // which slides in and out).
      this.onViewportChange = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.measure();
        });
      };
      window.addEventListener('scroll', this.onViewportChange, { passive: true });
      window.addEventListener('resize', this.onViewportChange);
      window.visualViewport?.addEventListener('resize', this.onViewportChange);

      const guards = document.querySelectorAll('[data-kevo-buy-guard]');
      if (guards.length) {
        this.guardObserver = new IntersectionObserver((entries) => {
          entries.forEach((entry) =>
            entry.isIntersecting ? this.visibleGuards.add(entry.target) : this.visibleGuards.delete(entry.target)
          );
          this.update();
        });
        guards.forEach((guard) => this.guardObserver.observe(guard));
      }

      // Focus: keep focused elements clear of the bar, and never hide the bar under the focus.
      this.onFocusIn = (event) => this.revealFocused(event.target);
      this.onFocusOut = () => requestAnimationFrame(() => this.update());
      document.addEventListener('focusin', this.onFocusIn);
      this.addEventListener('focusout', this.onFocusOut);

      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.onMediaChange = () => this.measure();
      this.mediaQuery.addEventListener('change', this.onMediaChange);

      this.measure();
    }

    disconnectedCallback() {
      this.guardObserver?.disconnect();
      window.removeEventListener('scroll', this.onViewportChange);
      window.removeEventListener('resize', this.onViewportChange);
      window.visualViewport?.removeEventListener('resize', this.onViewportChange);
      document.removeEventListener('focusin', this.onFocusIn);
      this.removeEventListener('focusout', this.onFocusOut);
      this.mediaQuery?.removeEventListener('change', this.onMediaChange);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.setScrollPadding(false);
      document.body.classList.remove('kevo-sticky-buy-visible');
    }

    barHeight() {
      return this.querySelector('.kevo-sticky-buy__inner')?.offsetHeight || this.offsetHeight || 0;
    }

    // Bottom edge of the sticky site header while it is on screen (0 when hidden / not sticky).
    headerBottom() {
      const header = document.querySelector('.section-header');
      if (!header) return 0;
      const position = getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.max(0, header.getBoundingClientRect().bottom);
    }

    measure() {
      const rect = this.mainButton.getBoundingClientRect();
      const visibleTop = this.headerBottom();
      const visibleBottom = window.innerHeight - this.barHeight();
      const overlap = Math.min(rect.bottom, visibleBottom) - Math.max(rect.top, visibleTop);
      const hasLayout = rect.width > 0 && rect.height > 0;
      this.mainClearlyVisible = hasLayout && overlap / rect.height >= 0.6;
      this.update();
    }

    update() {
      const enabledHere = !this.mediaQuery?.matches || this.classList.contains('kevo-sticky-buy--desktop');
      let show = enabledHere && !this.mainClearlyVisible && this.visibleGuards.size === 0;
      // Hiding makes the bar inert: never do that while it holds keyboard focus.
      if (!show && this.contains(document.activeElement)) show = true;
      if (show === this.classList.contains('is-visible')) return;

      this.classList.toggle('is-visible', show);
      this.inert = !show;
      document.body.classList.toggle('kevo-sticky-buy-visible', show);
      this.setScrollPadding(show);
    }

    // Lets the browser keep focused / scrolled-to elements above the bar.
    setScrollPadding(on) {
      const root = document.documentElement;
      if (on) {
        root.style.scrollPaddingBottom = `${this.barHeight() + 16}px`;
      } else {
        root.style.removeProperty('scroll-padding-bottom');
      }
    }

    // WCAG 2.4.11: if the newly focused element sits under the visible bar, scroll it above.
    revealFocused(target) {
      if (!this.classList.contains('is-visible') || !(target instanceof Element) || this.contains(target)) return;
      if (target === document.body || target.closest('cart-drawer, .drawer, details[open] .menu-drawer')) return;
      const rect = target.getBoundingClientRect();
      const barTop = window.innerHeight - this.barHeight();
      if (rect.bottom > barTop - 8 && rect.top < window.innerHeight) {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        window.scrollBy({ top: rect.bottom - barTop + 16, behavior: reduce ? 'auto' : 'smooth' });
      }
    }
  }

  customElements.define('kevo-sticky-buy', KevoStickyBuy);

  /*
    <kevo-bundles> Bundle cards are radio inputs named "quantity" attached to the main product form,
    so Dawn's add-to-cart sends the selected quantity. This element only keeps the UI in sync:
    the selected total (mirrored by the sticky bar) and the add-to-cart label with the bundle price.
  */
  class KevoBundles extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.onChange = this.onChange.bind(this);
      this.addEventListener('change', this.onChange);
      this.observeButton();
      this.sync();

      // Dawn's pubsub.js normally loads first; if this script runs earlier, subscribe once it has.
      const subscribeToVariants = () => {
        if (this.unsubscribe || typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => this.onVariantChange(event));
      };
      subscribeToVariants();
      if (!this.unsubscribe) document.addEventListener('DOMContentLoaded', subscribeToVariants, { once: true });
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      this.buttonObserver?.disconnect();
      this.unsubscribe?.();
      this.unsubscribe = null;
    }

    get submitButton() {
      return document.getElementById(`ProductSubmitButton-${this.sectionId}`);
    }

    get selected() {
      return this.querySelector('.kevo-bundle__input:checked');
    }

    onChange(event) {
      if (event.target.matches('.kevo-bundle__input')) this.sync();
    }

    // Prices are rendered by Liquid (Shopify money format) for the selected variant.
    onVariantChange({ data }) {
      if (!data || data.sectionId !== this.sectionId || !data.html) return;
      const fresh = data.html.getElementById(`KevoBundles-${this.sectionId}`);
      if (!fresh) return;
      const quantity = this.selected?.value;
      this.querySelector('.kevo-bundles__list').innerHTML = fresh.querySelector('.kevo-bundles__list').innerHTML;
      const keep = quantity && this.querySelector(`.kevo-bundle__input[value="${quantity}"]:not(:disabled)`);
      if (keep) keep.checked = true;
      this.sync();
    }

    observeButton() {
      const button = this.submitButton;
      if (!button) return;
      this.buttonObserver = new MutationObserver(() => this.applyLabel());
      this.buttonObserver.observe(button, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ['disabled'] });
    }

    labelText() {
      const total = this.selected?.dataset.total;
      const label = this.dataset.ctaLabel;
      if (!label) return null;
      return this.hasAttribute('data-cta-price') && total ? `${label} · ${total}` : label;
    }

    // Dawn rewrites the label on variant changes and after adding to cart; re-apply ours while the
    // button is available. Sold out / unavailable texts from Dawn are left untouched.
    applyLabel() {
      const button = this.submitButton;
      const span = button?.querySelector(':scope > span');
      const text = this.labelText();
      if (!span || !text || button.disabled) return;
      if (span.textContent.trim() !== text) span.textContent = text;
    }

    sync() {
      const total = this.selected?.dataset.total || '';
      const totalElement = document.getElementById(`KevoBundleTotal-${this.sectionId}`);
      if (totalElement && totalElement.textContent !== total) totalElement.textContent = total;
      this.applyLabel();
    }
  }

  customElements.define('kevo-bundles', KevoBundles);

  // Final CTA: scroll back to the bundle selector (or the main purchase area).
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-kevo-scroll-to-purchase]');
    if (!link) return;
    const target =
      document.querySelector('kevo-bundles') ||
      document.querySelector('product-info[id^="MainProduct-"] .product-form__buttons');
    if (!target) return;
    event.preventDefault();
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    target.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
    const focusTarget = target.querySelector('.kevo-bundle__input:checked') || target.querySelector('button, input');
    focusTarget?.focus({ preventScroll: true });
  });

  /*
    Gallery position dots for the KEVO product hero (mobile). Decorative: Dawn's slide counter and
    previous/next buttons remain the accessible controls. Reads the gallery slider's state only and
    never changes its behavior.
  */
  const initGalleryDots = (root = document) => {
    root.querySelectorAll('.kevo-product slider-component[id^="GalleryViewer-"]').forEach((gallery) => {
      const buttons = gallery.querySelector('.slider-buttons');
      if (!buttons || gallery.dataset.kevoDots === 'ready') return;
      gallery.dataset.kevoDots = 'ready';

      const dots = document.createElement('div');
      dots.className = 'kevo-gallery-dots';
      dots.setAttribute('aria-hidden', 'true');
      const counter = buttons.querySelector('.slider-counter');
      counter ? counter.after(dots) : buttons.append(dots);

      const render = () => {
        const count = (gallery.sliderItemsToShow || []).length;
        const current = Math.min(Math.max(gallery.currentPage || 1, 1), Math.max(count, 1));
        if (dots.childElementCount !== count) {
          dots.replaceChildren(
            ...Array.from({ length: count }, () => {
              const dot = document.createElement('span');
              dot.className = 'kevo-gallery-dots__dot';
              return dot;
            })
          );
        }
        Array.from(dots.children).forEach((dot, index) => dot.classList.toggle('is-active', index === current - 1));
      };

      gallery.addEventListener('slideChanged', render);
      new ResizeObserver(render).observe(gallery);
      render();
    });
  };

  initGalleryDots();
  document.addEventListener('shopify:section:load', (event) => initGalleryDots(event.target));
})();
